import { AIFeatureType, AIProcessingStep, useAIProgressMessages } from '../useAIProgressMessages'
import { FC, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Stack, Typography } from '@mui/material'

import styles from './PhotoStudioLoadingAnimation.module.sass'
import { useTranslation } from 'react-i18next'

interface PhotoStudioGeneratingProps {
  /** Text to display below the animation (a node for live text, e.g. the Lab progress). If not provided, will use default "Generating results..." */
  loadingText?: ReactNode
  /** Whether to show text below animation */
  showText?: boolean
  /** Width of the animation */
  width?: number | string
  /** Height of the animation */
  height?: number | string
  /** Whether to show blur overlay background */
  showBlurOverlay?: boolean
  /**
   * Preview image URL - when provided, the overlay is sized to exactly match the
   * rendered image content area (same as objectFit:contain at any container size).
   */
  previewImageUrl?: string
  /** AI Feature type - when provided, will use dynamic progress messages */
  featureType?: AIFeatureType
  /** Current processing step - used with featureType for step-specific messages */
  processingStep?: AIProcessingStep
  /** Whether to use dynamic progress messages (default: false for backwards compatibility) */
  useDynamicMessages?: boolean
  /** Extra content rendered under the text — the Lab engine's scenes, laid over the photo */
  details?: ReactNode
  /** Strength of the sweeping halo, 0 to 1; a change fades. The Lab engine dims it with no scrim under it */
  haloOpacity?: number
}

/**
 * Photo Studio generating component that combines animation and loading text
 * Used for processing/generating scenarios
 *
 * Supports two modes:
 * 1. Static mode (default): Shows a single loading text
 * 2. Dynamic mode: Shows rotating, feature-specific progress messages
 *
 * Static mode (backwards compatible)
 * <PhotoStudioGenerating showText={true} />
 * <PhotoStudioGenerating showText={true} loadingText="Custom message..." />
 *
 * Dynamic mode with auto-rotating messages
 * <PhotoStudioGenerating
 *   showText={true}
 *   featureType={AIFeatureType.MAGIC_ERASER}
 *   useDynamicMessages={true}
 * />
 * 
 * When previewImageUrl is provided, a ResizeObserver mirrors the objectFit:contain
 * sizing formula so the overlay covers exactly the photo, not the letterbox areas,
 * at every container size (big monitors, any browser zoom level, window resize).
 */
export const PhotoStudioGenerating: FC<PhotoStudioGeneratingProps> = ({
  loadingText,
  showText = true,
  width = '100%',
  height = '100%',
  showBlurOverlay = true,
  previewImageUrl,
  featureType,
  processingStep,
  useDynamicMessages = false,
  details,
  haloOpacity = 1,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])

  // Use dynamic progress messages when feature type is provided and dynamic mode is enabled
  const { currentMessage: dynamicMessage } = useAIProgressMessages({
    featureType: featureType ?? AIFeatureType.VIRTUAL_STAGING,
    isProcessing: useDynamicMessages,
    currentStep: processingStep,
  })

  const displayText = useMemo(() => {
    // If dynamic messages are enabled and we have a feature type, use dynamic message
    if (useDynamicMessages && featureType) return dynamicMessage
    // Otherwise use provided loadingText or default
    return loadingText ?? t('generating_results')
  }, [useDynamicMessages, featureType, dynamicMessage, loadingText, t])

  // Intrinsic width/height ratio of the preview image (NW / NH).
  // Read synchronously from browser cache on mount; falls back to async onload.
  const [imageRatio, setImageRatio] = useState<number | undefined>(() => {
    if (!previewImageUrl) return undefined
    const img = new Image()
    img.src = previewImageUrl
    if (img.complete && img.naturalWidth > 0) return img.naturalWidth / img.naturalHeight
    return undefined
  })

  useEffect(() => {
    if (!previewImageUrl) { setImageRatio(undefined); return }
    const img = new Image()
    img.src = previewImageUrl
    if (img.complete && img.naturalWidth > 0) {
      setImageRatio(img.naturalWidth / img.naturalHeight)
      return
    }
    img.onload = () => { if (img.naturalWidth > 0) setImageRatio(img.naturalWidth / img.naturalHeight) }
  }, [previewImageUrl])

  // Pixel dimensions of the overlay, recalculated whenever the container resizes.
  // Uses the same formula as objectFit:contain so the overlay always matches the photo area.
  const containerRef = useRef<HTMLDivElement>(null)
  const [overlaySize, setOverlaySize] = useState<{ w: number; h: number } | undefined>(undefined)

  useLayoutEffect(() => {
    if (!containerRef.current || imageRatio == null) { setOverlaySize(undefined); return }

    const calcSize = (cw: number, ch: number) =>
      cw / ch > imageRatio
        ? { w: ch * imageRatio, h: ch }   // container wider: height-constrained, letterbox sides
        : { w: cw, h: cw / imageRatio }   // container taller: width-constrained, letterbox top/bottom

    setOverlaySize(calcSize(containerRef.current.clientWidth, containerRef.current.clientHeight))

    const ro = new ResizeObserver((entries) => {
      const { width: rw, height: rh } = entries[0].contentRect
      setOverlaySize(calcSize(rw, rh))
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [imageRatio])

  // Shared overlay layers: blur + animation + text
  const overlayLayers = (
    <>
      {showBlurOverlay && (
        <Stack
          position='absolute'
          top={0}
          left={0}
          right={0}
          bottom={0}
          sx={{ backdropFilter: 'blur(4px)' }}
        />
      )}

      <Stack
        position='absolute'
        top={0}
        left={0}
        right={0}
        bottom={0}
        alignItems="center"
        justifyContent="center"
        sx={{ zIndex: 1300, pointerEvents: 'none' }}
      >
        {/* Loading animation */}
        <Stack
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 10,
            overflow: 'hidden',
            opacity: haloOpacity,
            transition: 'opacity .45s ease',
          }}
        >
          <div className={styles.stage}>
            <div className={`${styles.animatedElement} ${styles.layer}`} />
          </div>
        </Stack>

        {/* Loading text */}
        {showText && (
          <Typography variant='text-md' fontWeight='bold' color="white" mt={2} sx={{ zIndex: 1301 }}>
            {displayText}
          </Typography>
        )}
        {details}
      </Stack>
    </>
  )

  return (
    <Stack
      ref={containerRef}
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      width={width}
      height={height}
      alignItems="center"
      justifyContent="center"
      sx={{ zIndex: 1200, overflow: 'hidden' }}
    >
      {(previewImageUrl && overlaySize) ? (
        // Sized to exactly the rendered photo area — mirrors objectFit:contain geometry.
        // flexShrink:0 prevents the flex container from squishing the explicitly sized box.
        <Stack
          sx={{
            position: 'relative',
            width: overlaySize.w,
            height: overlaySize.h,
            flexShrink: 0,
            overflow: 'hidden',
            ...(showBlurOverlay && { backgroundColor: 'rgba(37, 42, 46, 0.4)' })
          }}
        >
          {overlayLayers}
        </Stack>
      ) : (
        // Fallback: fill the full container (no URL given or dimensions not yet known)
        <Stack
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          sx={{
            overflow: 'hidden',
            ...(showBlurOverlay && { backgroundColor: 'rgba(37, 42, 46, 0.4)' })
          }}
        >
          {overlayLayers}
        </Stack>
      )}
    </Stack>
  )
}
